import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Table,
  Modal,
  Descriptions,
  Button,
  Tag,
  Typography,
  Space,
  message,
  Statistic,
  Form,
  DatePicker,
  InputNumber,
  Switch,
} from "antd";
import type { TableProps } from "antd";
import { useGlobal } from "../../contexts/GlobalContext";
import { isSuperAdminRole } from "../../utils/roles";
import { vipLevelLabel, vipLevelColor, VIP_LEVEL_OPTIONS } from "../../utils/plan";
import axios from "axios";
import "remixicon/fonts/remixicon.css";
import dayjs from "dayjs";

const { Search } = Input;
const { Text } = Typography;

// 用户列表接口
interface UserListItem {
  open_id: number;
  email: string;
  role: string;
  created_at: string;
  is_vip: boolean;
  vip_level: number;
  vip_start: string;
  vip_end: string;
  token: number;
  github_bound: boolean;
  qq_bound: boolean;
}

interface UserListResponse {
  total: number;
  list: UserListItem[];
  stastistics: {
    total_users: number;
    total_vip_users: number;
    total_token_users: number;
  };
}

interface UserDetail {
  OpenID: number;
  QQUnionID: string;
  GHUnionID: string;
  Mail: string;
  CreatedAt: string;
  UpdatedAt: string;
  Role: string;
  Phone: string;
  post_chat: {
    ID: number;
    APIKey: string;
    OpenID: number;
    VIPStart: string;
    VIPEnd: string;
    VIPLevel: number;
    CreatedAt: string;
    UpdatedAt: string;
    Token: number;
  };
  projects: {
    ID: number;
    OpenID: number;
    Name: string;
    KeyValue: string;
    APIKey: string;
    Refer: string;
    URLBinding: number;
    NoticeLevel: number;
    MultiLanguage: number;
    IsPostchat: number;
    IsPublic: number;
    Prompt: string;
    Version: string;
    CreatedAt: string;
    UpdatedAt: string;
  }[];
}

interface UserData {
  key: string;
  uid: string;
  email: string;
  memberType: string;
  registerTime: string;
  isVip: boolean;
  vipLevel: number;
  vipEndTime: string;
  token: number;
  role: string;
}

interface WalletInfo {
  balance: number;
  admin_override_cap: number;
  frozen: boolean;
}

interface WalletAuditLog {
  id: number;
  action: string;
  delta: number;
  balance_before: number;
  balance_after: number;
  cap_before: number;
  cap_after: number;
  reason: string;
  created_at: string;
}

interface BanRecord {
  id: number;
  user_id: number;
  reason: string;
  operator_id: number;
  expires_at?: string | null;
  lifted_at?: string | null;
  lift_operator_id?: number;
  created_at: string;
}

const UserManagement: React.FC = () => {
  const { token, API_BASE_URL, user: currentAdmin } = useGlobal();
  // 重置密码/代签登录链接后端为 superadmin 专属,普通 admin 隐藏按钮而非点击后 403。
  const isSuperAdmin = isSuperAdminRole(currentAdmin?.apiInfo?.role);
  const [searchType, setSearchType] = useState<string>("open_id");
  const [searchValue, setSearchValue] = useState("");
  const [vipFilter, setVipFilter] = useState<boolean | undefined>(undefined);
  const [vipLevel, setVipLevel] = useState<number | undefined>(undefined);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<string | null>(null);
  const [isEditingToken, setIsEditingToken] = useState(false);
  const [isEditingVip, setIsEditingVip] = useState(false);
  const [tokenForm] = Form.useForm();
  const [vipForm] = Form.useForm();
  const [walletAdjustForm] = Form.useForm();
  const [walletCapForm] = Form.useForm();
  const [tokenUpdateLoading, setTokenUpdateLoading] = useState(false);
  const [vipUpdateLoading, setVipUpdateLoading] = useState(false);
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [walletAuditLogs, setWalletAuditLogs] = useState<WalletAuditLog[]>([]);
  const [walletActionLoading, setWalletActionLoading] = useState(false);
  const [banRecords, setBanRecords] = useState<BanRecord[]>([]);
  const [banActionLoading, setBanActionLoading] = useState(false);
  const [banForm] = Form.useForm();

  // 统计信息状态
  const [statistics, setStatistics] = useState({
    total_users: 0,
    total_vip_users: 0,
    total_token_users: 0,
  });

  // 获取会员类型文本（四档：免费/Pro/Pro Max/商业版）
  const getMemberTypeText = useCallback(
    (isVip: boolean, vipLevel: number): string => vipLevelLabel(vipLevel, isVip),
    []
  );

  // 获取用户列表
  const fetchUsers = useCallback(
    async ({
      current,
      pageSize,
      sortField,
      sortOrder,
    }: {
      current: number;
      pageSize: number;
      sortField: string | null;
      sortOrder: string | null;
    }) => {
      if (!token) return;

      setLoading(true);
      try {
        const params: any = {
          page: current,
          page_size: pageSize,
          order_by: sortField || "created_at",
          order: sortOrder || "desc",
        };

        // 添加筛选条件
        if (searchType === "email" && searchValue) {
          params.email = searchValue;
        } else if (searchType === "open_id" && searchValue) {
          params.open_id = parseInt(searchValue);
        } else if (searchType === "project_key" && searchValue) {
          params.project_key = searchValue;
        } else if (searchType === "refer" && searchValue) {
          params.refer = searchValue;
        }

        if (vipFilter !== undefined) {
          params.is_vip = vipFilter;
        }

        if (vipLevel !== undefined) {
          params.vip_level = vipLevel;
        }

        const response = await axios.get<{
          code: number;
          msg: string;
          data: UserListResponse;
        }>(`${API_BASE_URL}/api/v3/admin/user/list`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code === 200) {
          const userData = response.data.data.list.map((user) => ({
            key: user.open_id.toString(),
            uid: user.open_id.toString(),
            email: user.email,
            memberType: getMemberTypeText(user.is_vip, user.vip_level),
            registerTime: new Date(user.created_at).toLocaleString("zh-CN"),
            isVip: user.is_vip,
            vipLevel: user.vip_level,
            vipEndTime: user.vip_end,
            token: user.token,
            role: user.role,
          }));

          setUsers(userData);
          setTotal(response.data.data.total);

          // 设置统计数据
          if (response.data.data.stastistics) {
            setStatistics(response.data.data.stastistics);
          }
        } else {
          message.error(response.data.msg || "获取用户列表失败");
          setUsers([]);
        }
      } catch (error) {
        console.error("获取用户列表失败", error);
        message.error("获取用户列表失败，请稍后重试");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [
      token,
      API_BASE_URL,
      searchType,
      searchValue,
      vipFilter,
      vipLevel,
      getMemberTypeText,
    ]
  );

  // 初始加载和筛选条件变化时获取数据
  useEffect(() => {
    fetchUsers({
      current: currentPage,
      pageSize: pageSize,
      sortField,
      sortOrder,
    });
  }, [fetchUsers, currentPage, pageSize, sortField, sortOrder]);

  // 处理搜索
  const handleSearch = () => {
    setCurrentPage(1);
  };

  // 处理表格变化
  const handleTableChange: TableProps<UserData>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const newSortField =
      sorter && "field" in sorter ? sorter.field?.toString() : null;
    const newSortOrder =
      sorter && "order" in sorter
        ? sorter.order === "ascend"
          ? "asc"
          : "desc"
        : null;

    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    setSortField(
      newSortField === "registerTime"
        ? "created_at"
        : newSortField === "email"
        ? "email"
        : newSortField === "role"
        ? "role"
        : "created_at"
    );
    setSortOrder(newSortOrder || "desc");
  };

  const fetchUserBans = useCallback(
    async (uid: number | string) => {
      if (!token) return;
      try {
        const response = await axios.get<{
          code: number;
          msg: string;
          data: BanRecord[];
        }>(`${API_BASE_URL}/api/v3/admin/users/${uid}/bans`, {
          params: { limit: 20 },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.code === 200) {
          setBanRecords(response.data.data || []);
        }
      } catch (error) {
        console.error("获取封禁历史失败", error);
        message.warning("封禁历史加载失败，详情中的封禁信息可能不完整");
      }
    },
    [API_BASE_URL, token]
  );

  const getActiveBan = useCallback(() => {
    const now = new Date();
    return banRecords.find((item) => {
      if (item.lifted_at) return false;
      if (!item.expires_at) return true;
      return new Date(item.expires_at) > now;
    });
  }, [banRecords]);

  // 查看用户详情
  const handleViewDetail = async (record: UserData) => {
    if (!token) return;

    try {
      const response = await axios.get<{
        code: number;
        msg: string;
        data: UserDetail;
      }>(`${API_BASE_URL}/api/v3/admin/user/info`, {
        params: {
          openid: record.uid,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setCurrentUser(response.data.data);
        setIsDetailModalVisible(true);
        fetchWalletControl(response.data.data.OpenID);
        fetchUserBans(response.data.data.OpenID);

        setIsEditingToken(false);
        setIsEditingVip(false);

        // 初始化表单值
        const userData = response.data.data;
        tokenForm.setFieldsValue({
          token: userData.post_chat.Token,
        });

        vipForm.setFieldsValue({
          vip_level: userData.post_chat.VIPLevel,
          vip_start: userData.post_chat.VIPStart
            ? dayjs(userData.post_chat.VIPStart)
            : dayjs(),
          vip_end: userData.post_chat.VIPEnd
            ? dayjs(userData.post_chat.VIPEnd)
            : dayjs().add(1, "month"),
        });
      } else {
        message.error(response.data.msg || "获取用户详情失败");
      }
    } catch (error) {
      console.error("获取用户详情失败", error);
      message.error("获取用户详情失败，请稍后重试");
    }
  };

  const handleBanUser = () => {
    if (!currentUser) return;
    banForm.resetFields();
    Modal.confirm({
      title: "封禁用户",
      content: (
        <Form form={banForm} layout="vertical">
          <Form.Item
            name="reason"
            label="封禁原因"
            rules={[{ required: true, message: "请输入封禁原因" }]}
          >
            <Input.TextArea rows={3} placeholder="请输入封禁原因" />
          </Form.Item>
          <Form.Item name="expiresAt" label="到期时间">
            <DatePicker showTime style={{ width: "100%" }} placeholder="留空表示永久封禁" />
          </Form.Item>
        </Form>
      ),
      okText: "确认封禁",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        const values = await banForm.validateFields();
        setBanActionLoading(true);
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/v3/admin/users/${currentUser.OpenID}/ban`,
            {
              reason: values.reason,
              expiresAt: values.expiresAt ? values.expiresAt.toISOString() : "",
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.code === 200) {
            message.success("用户已封禁");
            fetchUserBans(currentUser.OpenID);
          } else {
            message.error(response.data.msg || "封禁失败");
          }
        } catch (error: any) {
          message.error(error?.response?.data?.msg || "封禁失败，请稍后重试");
          throw error;
        } finally {
          setBanActionLoading(false);
        }
      },
    });
  };

  const handleUnbanUser = () => {
    if (!currentUser) return;
    Modal.confirm({
      title: "确认解封用户",
      content: `确定要解封 UID ${currentUser.OpenID} 吗？`,
      okText: "解封",
      cancelText: "取消",
      onOk: async () => {
        setBanActionLoading(true);
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/v3/admin/users/${currentUser.OpenID}/unban`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.code === 200) {
            message.success("用户已解封");
            fetchUserBans(currentUser.OpenID);
          } else {
            message.error(response.data.msg || "解封失败");
          }
        } catch (error: any) {
          message.error(error?.response?.data?.msg || "解封失败，请稍后重试");
        } finally {
          setBanActionLoading(false);
        }
      },
    });
  };

  // 重置密码
  const handleResetPassword = async () => {
    if (!currentUser) return;

    Modal.confirm({
      title: "确认重置密码",
      content: "是否确认重置该用户的密码？重置后将发送新密码到用户邮箱。",
      onOk: async () => {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/v3/admin/user/resetPassword`,
            {
              openid: currentUser.OpenID,
              send_notice: true,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data.code === 200) {
            Modal.success({
              title: "密码重置成功",
              content: (
                <div>
                  <p>新密码已发送至用户邮箱</p>
                  <p>
                    新密码：
                    <Text copyable style={{ marginLeft: 8 }}>
                      {response.data.data.new_password}
                    </Text>
                  </p>
                </div>
              ),
            });
          } else {
            message.error(response.data.msg || "密码重置失败");
          }
        } catch (error) {
          console.error("密码重置失败", error);
          message.error("密码重置失败，请稍后重试");
        }
      },
    });
  };

  // 获取用户JWT令牌和OAuth Code
  const getUserLoginToken = async (openId: string) => {
    if (!token) return null;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/admin/getJWT`,
        { openID: openId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        message.error(response.data.msg || "获取登录令牌失败");
        return null;
      }
    } catch (error) {
      console.error("获取登录令牌失败", error);
      message.error("获取登录令牌失败，请稍后重试");
      return null;
    }
  };

  const parseTokenValue = (value: string | undefined): number => {
    if (!value) return 0;
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const fetchWalletControl = useCallback(
    async (uid: number | string) => {
      if (!token) return;
      try {
        const [walletRes, auditRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/v3/admin/users/${uid}/wallet`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `${API_BASE_URL}/api/v3/admin/users/${uid}/wallet/audit-logs`,
            {
              params: { page: 1, pageSize: 10 },
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
        ]);
        if (walletRes.data.code === 200) {
          const data = walletRes.data.data;
          setWalletInfo({
            balance: data.balance || 0,
            admin_override_cap: data.admin_override_cap || 0,
            frozen: !!data.frozen,
          });
          walletCapForm.setFieldsValue({ cap: data.admin_override_cap || 0 });
        }
        if (auditRes.data.code === 200) {
          setWalletAuditLogs(auditRes.data.data.items || []);
        }
      } catch (error) {
        console.error("获取钱包调控信息失败", error);
        message.warning("钱包信息加载失败，余额/冻结状态可能不是最新");
      }
    },
    [API_BASE_URL, token, walletCapForm]
  );

  // 提交 Token 余额更新
  const handleTokenUpdate = async (values: { token: number }) => {
    if (!currentUser) return;

    setTokenUpdateLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/admin/user/balance`,
        {
          open_id: currentUser.OpenID,
          token: values.token,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("Token 余额更新成功");
        setIsEditingToken(false);

        // 更新当前用户数据
        if (currentUser) {
          setCurrentUser({
            ...currentUser,
            post_chat: {
              ...currentUser.post_chat,
              Token: values.token,
            },
          });
        }

        fetchUsers({
          current: currentPage,
          pageSize: pageSize,
          sortField,
          sortOrder,
        });
      } else {
        message.error(response.data.msg || "Token 余额更新失败");
      }
    } catch (error) {
      console.error("Token 余额更新失败", error);
      message.error("Token 余额更新失败，请稍后重试");
    } finally {
      setTokenUpdateLoading(false);
    }
  };

  const handleWalletAdjust = async (values: { delta: number; reason?: string }) => {
    if (!currentUser) return;
    setWalletActionLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/admin/users/${currentUser.OpenID}/wallet/adjust`,
        { delta: values.delta, reason: values.reason || "管理员手动调账" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.code === 200) {
        message.success("钱包余额已调整");
        walletAdjustForm.resetFields();
        const data = response.data.data;
        setWalletInfo({
          balance: data.balance || 0,
          admin_override_cap: data.admin_override_cap || 0,
          frozen: !!data.frozen,
        });
        setCurrentUser({
          ...currentUser,
          post_chat: { ...currentUser.post_chat, Token: data.balance || 0 },
        });
        fetchWalletControl(currentUser.OpenID);
        fetchUsers({ current: currentPage, pageSize, sortField, sortOrder });
      } else {
        message.error(response.data.msg || "钱包调整失败");
      }
    } catch (error) {
      message.error("钱包调整失败，请稍后重试");
    } finally {
      setWalletActionLoading(false);
    }
  };

  const handleWalletCap = async (values: { cap: number; reason?: string }) => {
    if (!currentUser) return;
    setWalletActionLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/v3/admin/users/${currentUser.OpenID}/wallet/cap`,
        { cap: values.cap || 0, reason: values.reason || "管理员设置月度上限" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.code === 200) {
        message.success("月度上限已更新");
        const data = response.data.data;
        setWalletInfo({
          balance: data.balance || 0,
          admin_override_cap: data.admin_override_cap || 0,
          frozen: !!data.frozen,
        });
        fetchWalletControl(currentUser.OpenID);
      } else {
        message.error(response.data.msg || "月度上限更新失败");
      }
    } catch (error) {
      message.error("月度上限更新失败，请稍后重试");
    } finally {
      setWalletActionLoading(false);
    }
  };

  const handleWalletFrozen = async (frozen: boolean) => {
    if (!currentUser) return;
    setWalletActionLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/v3/admin/users/${currentUser.OpenID}/wallet/frozen`,
        { frozen, reason: frozen ? "管理员冻结钱包" : "管理员解冻钱包" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.code === 200) {
        message.success(frozen ? "钱包已冻结" : "钱包已解冻");
        const data = response.data.data;
        setWalletInfo({
          balance: data.balance || 0,
          admin_override_cap: data.admin_override_cap || 0,
          frozen: !!data.frozen,
        });
        fetchWalletControl(currentUser.OpenID);
      } else {
        message.error(response.data.msg || "钱包状态更新失败");
      }
    } catch (error) {
      message.error("钱包状态更新失败，请稍后重试");
    } finally {
      setWalletActionLoading(false);
    }
  };

  // 提交 VIP 信息更新
  const handleVipUpdate = async (values: {
    vip_level: number;
    vip_start: dayjs.Dayjs | null;
    vip_end: dayjs.Dayjs | null;
  }) => {
    if (!currentUser) return;

    setVipUpdateLoading(true);
    try {
      // 如果是普通用户，设置为VIP1但过期时间为现在
      const now = dayjs();
      const requestData = {
        open_id: currentUser.OpenID,
        vip_level: values.vip_level === 0 ? 1 : values.vip_level,
        vip_start:
          values.vip_level === 0
            ? now.format("YYYY-MM-DDTHH:mm:ssZ")
            : values.vip_start?.format("YYYY-MM-DDTHH:mm:ssZ") ||
              now.format("YYYY-MM-DDTHH:mm:ssZ"),
        vip_end:
          values.vip_level === 0
            ? now.format("YYYY-MM-DDTHH:mm:ssZ")
            : values.vip_end?.format("YYYY-MM-DDTHH:mm:ssZ") ||
              now.format("YYYY-MM-DDTHH:mm:ssZ"),
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/v3/admin/user/vip`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("VIP 信息更新成功");
        setIsEditingVip(false);

        // 更新当前用户数据
        if (currentUser) {
          setCurrentUser({
            ...currentUser,
            post_chat: {
              ...currentUser.post_chat,
              VIPLevel: values.vip_level,
              VIPStart: requestData.vip_start,
              VIPEnd: requestData.vip_end,
            },
          });
        }

        fetchUsers({
          current: currentPage,
          pageSize: pageSize,
          sortField,
          sortOrder,
        });
      } else {
        message.error(response.data.msg || "VIP 信息更新失败");
      }
    } catch (error) {
      console.error("VIP 信息更新失败", error);
      message.error("VIP 信息更新失败，请稍后重试");
    } finally {
      setVipUpdateLoading(false);
    }
  };

  const columns: TableProps<UserData>["columns"] = [
    {
      title: "UID",
      dataIndex: "uid",
      key: "uid",
    },
    {
      title: "邮箱",
      dataIndex: "email",
      key: "email",
      sorter: true,
      render: (email: string, record: UserData) => (
        <Space>
          {record.role === "admin" && <Tag color="red">管理员</Tag>}
          {email || "-"}
        </Space>
      ),
    },
    {
      title: "会员类型",
      dataIndex: "memberType",
      key: "memberType",
      render: (text: string, record: UserData) => (
        <Tag color={vipLevelColor(record.vipLevel, record.isVip)}>{text}</Tag>
      ),
    },
    {
      title: "Token余额",
      dataIndex: "token",
      key: "token",
      render: (token: number) => token.toLocaleString(),
    },
    {
      title: "注册时间",
      dataIndex: "registerTime",
      key: "registerTime",
      sorter: true,
    },
    {
      title: "操作",
      key: "action",
      render: (_: unknown, record: UserData) => (
        <Button
          type="link"
          onClick={() => handleViewDetail(record)}
          style={{ padding: 0 }}
        >
          详情
        </Button>
      ),
    },
  ];

  const userDetailModal = (
    <Modal
      getContainer={false}
      title="用户详情"
      open={isDetailModalVisible}
      onCancel={() => setIsDetailModalVisible(false)}
      footer={null}
      width={800}
      maskClosable={true}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "calc(100vh - 130px)",
          overflowY: "auto",
          overflowX: "hidden",
        },
      }}
    >
      {currentUser && (
        <>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="UID" span={1}>
              {currentUser.OpenID}
            </Descriptions.Item>
            <Descriptions.Item label="邮箱" span={1}>
              {currentUser.Mail}
            </Descriptions.Item>
            <Descriptions.Item label="注册时间" span={1}>
              {new Date(currentUser.CreatedAt).toLocaleString("zh-CN")}
            </Descriptions.Item>
            <Descriptions.Item label="最后更新时间" span={1}>
              {new Date(currentUser.UpdatedAt).toLocaleString("zh-CN")}
            </Descriptions.Item>
            <Descriptions.Item label="角色" span={1}>
              <Tag color={currentUser.Role === "admin" ? "red" : "default"}>
                {currentUser.Role === "admin" ? "管理员" : "普通用户"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="社交账号绑定" span={1}>
              <Space>
                <Tag color={currentUser.QQUnionID ? "blue" : "default"}>
                  {currentUser.QQUnionID ? "已绑定QQ" : "未绑定QQ"}
                </Tag>
                <Tag color={currentUser.GHUnionID ? "blue" : "default"}>
                  {currentUser.GHUnionID ? "已绑定GitHub" : "未绑定GitHub"}
                </Tag>
              </Space>
            </Descriptions.Item>
          </Descriptions>

          <Card size="small" variant="outlined" style={{ marginTop: 16 }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={16}>
                <Space direction="vertical" size={4}>
                  <Space wrap>
                    <Text strong>账号状态</Text>
                    {getActiveBan() ? (
                      <Tag color="red">已封禁</Tag>
                    ) : (
                      <Tag color="green">正常</Tag>
                    )}
                  </Space>
                  {getActiveBan() && (
                    <Text type="secondary">
                      原因：{getActiveBan()?.reason || "-"}；到期：
                      {getActiveBan()?.expires_at
                        ? new Date(getActiveBan()!.expires_at!).toLocaleString("zh-CN")
                        : "永久"}
                    </Text>
                  )}
                </Space>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: "right" }}>
                {getActiveBan() ? (
                  <Button onClick={handleUnbanUser} loading={banActionLoading}>
                    解封用户
                  </Button>
                ) : (
                  <Button danger onClick={handleBanUser} loading={banActionLoading}>
                    封禁用户
                  </Button>
                )}
              </Col>
            </Row>
          </Card>

          <Typography.Title level={5} style={{ margin: "24px 0 16px" }}>
            <i className="ri-vip-crown-line" /> PostChat信息
          </Typography.Title>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="API Key" span={2}>
              <Text copyable={{ text: currentUser.post_chat.APIKey }}>
                {currentUser.post_chat.APIKey}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="会员等级" span={1}>
              {isEditingVip ? (
                <Form
                  form={vipForm}
                  layout="vertical"
                  onFinish={handleVipUpdate}
                  style={{ marginBottom: 0 }}
                >
                  <Form.Item
                    name="vip_level"
                    label="会员等级"
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true, message: "请选择会员等级" }]}
                  >
                    <Select style={{ width: "100%" }} options={VIP_LEVEL_OPTIONS} />
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.vip_level !== currentValues.vip_level
                    }
                  >
                    {({ getFieldValue }) => {
                      const vipLevel = getFieldValue("vip_level");
                      return vipLevel > 0 ? (
                        <div style={{ display: "flex", gap: 8 }}>
                          <Form.Item
                            name="vip_start"
                            label="会员开始时间"
                            style={{ marginBottom: 8, flex: 1 }}
                            rules={[
                              { required: true, message: "请选择开始时间" },
                            ]}
                          >
                            <DatePicker showTime style={{ width: "100%" }} />
                          </Form.Item>
                          <Form.Item
                            name="vip_end"
                            label="会员结束时间"
                            style={{ marginBottom: 8, flex: 1 }}
                            rules={[
                              { required: true, message: "请选择结束时间" },
                            ]}
                          >
                            <DatePicker showTime style={{ width: "100%" }} />
                          </Form.Item>
                        </div>
                      ) : null;
                    }}
                  </Form.Item>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                    }}
                  >
                    <Button
                      onClick={() => setIsEditingVip(false)}
                      disabled={vipUpdateLoading}
                    >
                      取消
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={vipUpdateLoading}
                    >
                      保存
                    </Button>
                  </div>
                </Form>
              ) : (
                <>
                  {(() => {
                    const expired = new Date(currentUser.post_chat.VIPEnd) < new Date();
                    const effLevel = expired ? 0 : currentUser.post_chat.VIPLevel;
                    return (
                      <Tag color={vipLevelColor(effLevel)}>{vipLevelLabel(effLevel)}</Tag>
                    );
                  })()}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setIsEditingVip(true)}
                    style={{ marginLeft: 8 }}
                    icon={<i className="ri-edit-line" />}
                  >
                    编辑
                  </Button>
                </>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Token余额" span={1}>
              {isEditingToken ? (
                <Form
                  form={tokenForm}
                  layout="inline"
                  onFinish={handleTokenUpdate}
                  style={{ marginBottom: 0 }}
                >
                  <Form.Item
                    name="token"
                    rules={[{ required: true, message: "请输入Token数量" }]}
                    style={{ marginBottom: 0, marginRight: 8 }}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: 150 }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={parseTokenValue}
                    />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Space>
                      <Button
                        onClick={() => setIsEditingToken(false)}
                        disabled={tokenUpdateLoading}
                      >
                        取消
                      </Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={tokenUpdateLoading}
                      >
                        保存
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              ) : (
                <>
                  {currentUser.post_chat.Token.toLocaleString()}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setIsEditingToken(true)}
                    style={{ marginLeft: 8 }}
                    icon={<i className="ri-edit-line" />}
                  >
                    编辑
                  </Button>
                </>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="会员有效期" span={2}>
              {isEditingVip ? null : (
                <>
                  {new Date(currentUser.post_chat.VIPStart).toLocaleString(
                    "zh-CN"
                  )}{" "}
                  至{" "}
                  {new Date(currentUser.post_chat.VIPEnd).toLocaleString(
                    "zh-CN"
                  )}
                </>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Typography.Title level={5} style={{ margin: "24px 0 16px" }}>
            <i className="ri-coin-line" /> 钱包调控
          </Typography.Title>
          <Card size="small" variant="outlined">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Statistic
                  title="当前余额"
                  value={walletInfo?.balance ?? currentUser.post_chat.Token}
                  suffix="tokens"
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="月度硬上限"
                  value={walletInfo?.admin_override_cap || 0}
                  suffix={(walletInfo?.admin_override_cap || 0) > 0 ? "tokens" : "不限"}
                />
              </Col>
              <Col xs={24} md={8}>
                <Space direction="vertical" size={4}>
                  <Text type="secondary">钱包状态</Text>
                  <Space>
                    <Switch
                      checked={!!walletInfo?.frozen}
                      loading={walletActionLoading}
                      onChange={handleWalletFrozen}
                    />
                    <Tag color={walletInfo?.frozen ? "red" : "green"}>
                      {walletInfo?.frozen ? "已冻结" : "正常"}
                    </Tag>
                  </Space>
                </Space>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} md={12}>
                <Form form={walletAdjustForm} layout="inline" onFinish={handleWalletAdjust}>
                  <Form.Item
                    name="delta"
                    rules={[{ required: true, message: "请输入调整量" }]}
                    style={{ flex: 1, marginBottom: 8 }}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="正数增加，负数扣除"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={parseTokenValue}
                    />
                  </Form.Item>
                  <Form.Item name="reason" style={{ flex: 1, marginBottom: 8 }}>
                    <Input placeholder="原因" />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 8 }}>
                    <Button type="primary" htmlType="submit" loading={walletActionLoading}>
                      调整余额
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
              <Col xs={24} md={12}>
                <Form form={walletCapForm} layout="inline" onFinish={handleWalletCap}>
                  <Form.Item name="cap" style={{ flex: 1, marginBottom: 8 }}>
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      placeholder="0 表示不限"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={parseTokenValue}
                    />
                  </Form.Item>
                  <Form.Item name="reason" style={{ flex: 1, marginBottom: 8 }}>
                    <Input placeholder="原因" />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 8 }}>
                    <Button htmlType="submit" loading={walletActionLoading}>
                      设置上限
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
            </Row>

            <Table<WalletAuditLog>
              style={{ marginTop: 12 }}
              size="small"
              rowKey="id"
              dataSource={walletAuditLogs}
              pagination={false}
              columns={[
                {
                  title: "时间",
                  dataIndex: "created_at",
                  render: (value: string) =>
                    value ? new Date(value).toLocaleString("zh-CN") : "-",
                },
                { title: "动作", dataIndex: "action" },
                {
                  title: "余额变化",
                  dataIndex: "delta",
                  render: (value: number) => (
                    <Tag color={value >= 0 ? "green" : "red"}>{value.toLocaleString()}</Tag>
                  ),
                },
                {
                  title: "余额",
                  render: (_: unknown, record: WalletAuditLog) =>
                    `${record.balance_before.toLocaleString()} → ${record.balance_after.toLocaleString()}`,
                },
                {
                  title: "上限",
                  render: (_: unknown, record: WalletAuditLog) =>
                    `${record.cap_before.toLocaleString()} → ${record.cap_after.toLocaleString()}`,
                },
                { title: "原因", dataIndex: "reason" },
              ]}
            />
          </Card>

          <Typography.Title level={5} style={{ margin: "24px 0 16px" }}>
            <i className="ri-forbid-line" /> 封禁历史
          </Typography.Title>
          <Table<BanRecord>
            size="small"
            rowKey="id"
            dataSource={banRecords}
            pagination={false}
            columns={[
              {
                title: "时间",
                dataIndex: "created_at",
                render: (value: string) =>
                  value ? new Date(value).toLocaleString("zh-CN") : "-",
              },
              {
                title: "状态",
                render: (_: unknown, record: BanRecord) => {
                  const active =
                    !record.lifted_at &&
                    (!record.expires_at || new Date(record.expires_at) > new Date());
                  return (
                    <Tag color={active ? "red" : "default"}>
                      {active ? "生效中" : "已结束"}
                    </Tag>
                  );
                },
              },
              { title: "原因", dataIndex: "reason" },
              {
                title: "到期",
                dataIndex: "expires_at",
                render: (value?: string | null) =>
                  value ? new Date(value).toLocaleString("zh-CN") : "永久",
              },
              {
                title: "解封时间",
                dataIndex: "lifted_at",
                render: (value?: string | null) =>
                  value ? new Date(value).toLocaleString("zh-CN") : "-",
              },
            ]}
          />

          <Typography.Title level={5} style={{ margin: "24px 0 16px" }}>
            <i className="ri-folder-line" /> 项目列表
          </Typography.Title>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {currentUser.projects?.length > 0 ? (
              currentUser.projects.map((project) => (
                <Card
                  key={project.ID}
                  title={
                    <Space>
                      <i className="ri-folder-line" />
                      {project.Name}
                    </Space>
                  }
                  size="small"
                >
                  <Descriptions
                    column={2}
                    bordered
                    size="small"
                    labelStyle={{ minWidth: "120px" }}
                  >
                    <Descriptions.Item label="项目KEY" span={2}>
                      <Text copyable={{ text: project.KeyValue }}>
                        {project.KeyValue}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="API Key" span={2}>
                      <Text copyable={{ text: project.APIKey }}>
                        {project.APIKey}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="计费类型" span={1}>
                      <Tag color={project.IsPostchat === 1 ? "blue" : "green"}>
                        {project.IsPostchat === 1 ? "时长计费" : "Token计费"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="项目属性" span={1}>
                      <Space>
                        <Tag
                          color={project.URLBinding === 1 ? "blue" : "default"}
                        >
                          {project.URLBinding === 1 ? "URL绑定" : "无URL绑定"}
                        </Tag>
                        <Tag
                          color={
                            project.MultiLanguage === 1 ? "blue" : "default"
                          }
                        >
                          {project.MultiLanguage === 1 ? "多语言" : "中文"}
                        </Tag>
                        <Tag
                          color={project.IsPublic === 1 ? "blue" : "default"}
                        >
                          {project.IsPublic === 1
                            ? "允许公开请求"
                            : "不允许公开请求"}
                        </Tag>
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="绑定域名" span={2}>
                      {project.Refer
                        ? project.Refer.split(",").map((domain) => (
                            <div key={domain}>{domain}</div>
                          ))
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="系统提示词" span={2}>
                      {project.Prompt || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="创建时间" span={1}>
                      {new Date(project.CreatedAt).toLocaleString("zh-CN")}
                    </Descriptions.Item>
                    <Descriptions.Item label="更新时间" span={1}>
                      {new Date(project.UpdatedAt).toLocaleString("zh-CN")}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))
            ) : (
              <div style={{ textAlign: "center", color: "#999" }}>暂无项目</div>
            )}
          </div>

          <div
            style={{
              marginTop: "24px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Space>
              {isSuperAdmin && (
                <Button
                  type="primary"
                  danger
                  onClick={handleResetPassword}
                  icon={<i className="ri-lock-password-line" />}
                >
                  重置密码
                </Button>
              )}
              {isSuperAdmin && (
                <Button
                  onClick={async () => {
                    const loginData = await getUserLoginToken(
                      currentUser.OpenID.toString()
                    );
                    if (loginData) {
                      const { token: userToken, oauth_code } = loginData;
                      const loginUrl = `${window.location.origin}/console/login?token=${userToken}&code=${oauth_code}`;
                      navigator.clipboard.writeText(loginUrl);
                      message.success("登录链接已复制到剪贴板");
                    }
                  }}
                  icon={<i className="ri-links-line" />}
                >
                  复制账户登录链接
                </Button>
              )}
            </Space>
          </div>
        </>
      )}
    </Modal>
  );

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      {/* 统计数据 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title={
                <>
                  总用户数 <i className="ri-user-line" />
                </>
              }
              value={statistics.total_users}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title={
                <>
                  付费会员数 <i className="ri-vip-crown-line" />
                </>
              }
              value={statistics.total_vip_users}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title={
                <>
                  Token用户总数 <i className="ri-coin-line" />
                </>
              }
              value={statistics.total_token_users}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索筛选 */}
      <Card
        title={
          <span>
            <i
              className="ri-filter-3-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            筛选条件
          </span>
        }
        style={{ marginTop: "16px", marginBottom: "16px" }}
        variant="outlined"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Space.Compact style={{ width: "100%" }}>
              <Select
                style={{ width: "120px" }}
                value={searchType}
                onChange={setSearchType}
                options={[
                  { value: "open_id", label: "UID" },
                  { value: "email", label: "邮箱" },
                  { value: "project_key", label: "项目Key" },
                  { value: "refer", label: "绑定网站" },
                ]}
              />
              <Search
                placeholder={
                  searchType === "email"
                    ? "请输入邮箱"
                    : searchType === "open_id"
                    ? "请输入UID"
                    : searchType === "project_key"
                    ? "请输入项目Key"
                    : "请输入绑定网站"
                }
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ width: "calc(100% - 120px)" }}
                onSearch={handleSearch}
              />
            </Space.Compact>
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="选择会员状态"
              style={{ width: "100%" }}
              value={vipFilter === undefined ? undefined : vipFilter}
              onChange={(value) => {
                setVipFilter(value === undefined ? undefined : value);
                setCurrentPage(1);
              }}
              options={[
                { value: undefined, label: "全部" },
                { value: false, label: "免费" },
                { value: true, label: "付费会员" },
              ]}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="选择会员等级"
              style={{ width: "100%" }}
              value={vipLevel}
              onChange={(value) => {
                setVipLevel(value);
                setCurrentPage(1);
              }}
              options={[{ value: undefined, label: "全部" }, ...VIP_LEVEL_OPTIONS.filter((o) => o.value > 0)]}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      {/* 用户列表 */}
      <Card
        title={
          <span>
            <i
              className="ri-user-search-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            用户列表
          </span>
        }
        variant="outlined"
        styles={{
          body: { padding: 8 },
        }}
      >
        <Table<UserData>
          columns={columns}
          dataSource={users}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          loading={loading}
          onChange={handleTableChange}
          rowKey="uid"
        />
      </Card>

      {userDetailModal}
    </div>
  );
};

export default UserManagement;
